import { VolumetricQuality } from './VolumetricPass';

export type GraphicsSettings = {
  renderScale: number;
  shadowMapResolution: number;
  pointShadowMapResolution: number;
  shadowBudget: number;
  ssaoEnabled: boolean;
  godRaysEnabled: boolean;
  volumetricEnabled: boolean;
  aerialPerspectiveEnabled: boolean;
  cloudVolumesEnabled: boolean;
  fxaaEnabled: boolean;
  taaEnabled: boolean;
  ssaoResolutionDivisor: number;
  godRaysResolutionDivisor: number;
  volumetricQuality: VolumetricQuality;
  /**
   * Multiplier applied to all LOD distance thresholds defined in the scene
   * manifest.  1.0 = use manifest distances as-is. Lower values switch to
   * cheaper LOD levels sooner (better perf), higher values keep detail longer.
   */
  lodDistanceScale: number;
  /** Enables dithered crossfade between LOD levels to reduce popping. */
  lodCrossfadeEnabled: boolean;
  bloomEnabled: boolean;
  bloomThreshold: number;
  bloomStrength: number;
  /**
   * Linear-to-display exposure multiplier applied by `FinalPresentPass`
   * before ACES tonemap. Independent of bloom — applies at every preset.
   */
  exposure: number;
};

export function defaultGraphicsSettings(): GraphicsSettings {
  return {
    renderScale: 1.0,
    shadowMapResolution: 2048,
    pointShadowMapResolution: 1024,
    shadowBudget: 4,
    ssaoEnabled: true,
    godRaysEnabled: true,
    volumetricEnabled: true,
    aerialPerspectiveEnabled: true,
    cloudVolumesEnabled: true,
    fxaaEnabled: false,
    taaEnabled: false,
    ssaoResolutionDivisor: 2,
    godRaysResolutionDivisor: 4,
    volumetricQuality: 'medium',
    lodDistanceScale: 1.0,
    lodCrossfadeEnabled: true,
    bloomEnabled: false,
    bloomThreshold: 1.0,
    bloomStrength: 0.04,
    exposure: 1.0,
  };
}

export type GraphicsPreset = 'low' | 'medium' | 'high' | 'ultra';

export const GRAPHICS_PRESETS: GraphicsPreset[] = ['low', 'medium', 'high', 'ultra'];

export function graphicsPreset(preset: GraphicsPreset): GraphicsSettings {
  switch (preset) {
    case 'low':
      return {
        renderScale: 0.5,
        shadowMapResolution: 512,
        pointShadowMapResolution: 512,
        shadowBudget: 2,
        ssaoEnabled: false,
        godRaysEnabled: false,
        volumetricEnabled: false,
        aerialPerspectiveEnabled: false,
        cloudVolumesEnabled: false,
        fxaaEnabled: false,
        taaEnabled: false,
        ssaoResolutionDivisor: 2,
        godRaysResolutionDivisor: 4,
        volumetricQuality: 'low',
        lodDistanceScale: 0.5,
        lodCrossfadeEnabled: false,
        bloomEnabled: false,
        bloomThreshold: 1.0,
        bloomStrength: 0.04,
        exposure: 1.0,
      };
    case 'medium':
      return defaultGraphicsSettings();
    case 'high':
      return {
        renderScale: 1.0,
        shadowMapResolution: 4096,
        pointShadowMapResolution: 1024,
        shadowBudget: 4,
        ssaoEnabled: true,
        godRaysEnabled: true,
        volumetricEnabled: true,
        aerialPerspectiveEnabled: true,
        cloudVolumesEnabled: true,
        fxaaEnabled: true,
        taaEnabled: true,
        ssaoResolutionDivisor: 1,
        godRaysResolutionDivisor: 4,
        volumetricQuality: 'high',
        lodDistanceScale: 1.5,
        lodCrossfadeEnabled: true,
        bloomEnabled: true,
        bloomThreshold: 1.0,
        bloomStrength: 0.04,
        exposure: 1.0,
      };
    case 'ultra':
      return {
        // 1.0 (not 1.5) — true supersampling is a ~2.25× fragment cost for
        // a barely-visible quality bump over High, and dominates GPU time
        // on high-DPI displays (notably Apple Silicon, where every fragment
        // pass is bandwidth-bound). TAA + FXAA already cover AA. Ultra
        // differentiates from High through shadow res, point-light shadow
        // budget, god-rays divisor, and LOD distance — not pixel count.
        renderScale: 1.0,
        shadowMapResolution: 4096,
        pointShadowMapResolution: 2048,
        shadowBudget: 8,
        ssaoEnabled: true,
        godRaysEnabled: true,
        volumetricEnabled: true,
        aerialPerspectiveEnabled: true,
        cloudVolumesEnabled: true,
        fxaaEnabled: true,
        taaEnabled: true,
        ssaoResolutionDivisor: 1,
        godRaysResolutionDivisor: 2,
        volumetricQuality: 'high',
        lodDistanceScale: 3.0,
        lodCrossfadeEnabled: true,
        bloomEnabled: true,
        bloomThreshold: 0.8,
        bloomStrength: 0.05,
        exposure: 1.0,
      };
  }
}

export function detectPreset(settings: GraphicsSettings): GraphicsPreset | null {
  for (const preset of GRAPHICS_PRESETS) {
    const ref = graphicsPreset(preset);
    const match = (Object.keys(ref) as (keyof GraphicsSettings)[]).every(
      (k) => settings[k] === ref[k],
    );
    if (match) return preset;
  }
  return null;
}
